import { useEffect, ComponentType } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProfileStep, setProfileCompleteness, setUser } from '@/slice/user';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Consultant, Franchise, User } from '@/types';
import { getProfile } from '@/api/user';
import { profileQueryKey } from '@/helpers/auth';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { FullScreenLoader } from '../ui/FullScreenLoader';
import { ROUTES } from '@/types/routes';
import { isFeeFilled } from '@/utils/feeValue';

export function withAuth<P extends JSX.IntrinsicAttributes>(
  WrappedComponent: ComponentType<P>,
) {
  return function Wrapper(props: P) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    // Handling Public Access
    const publicRoutePatterns = [
      /^\/consultants\/[^\/]+$/,
      /^\/franchisors\/[^\/]+$/,
    ];

    const isPublic = publicRoutePatterns.some((pattern) =>
      pattern.test(location.pathname),
    );

    const {
      data: user,
      isLoading,
      isError,
    } = useQuery<User, Error>({
      queryKey: profileQueryKey(),
      queryFn: () => getProfile(),
      retry: 1,
      enabled: !!localStorage.getItem('accessToken') || !isPublic,
    });

    useEffect(() => {
      const token = localStorage.getItem('accessToken');

      if (!token && !isPublic) {
        navigate(ROUTES.SIGN_IN);
        return;
      }

      if (!isPublic && !user && !isLoading && isError) {
        navigate(ROUTES.SIGN_IN);
      } else if (user) {
        dispatch(setUser(user));
      }
    }, [user, navigate, isLoading, dispatch, isPublic, isError]);

    useEffect(() => {
      if (user) {
        if (user.role === 'franchisor') {
          const franchise = user as Franchise;
          const profileCompleteness: ProfileStep[] = [
            {
              title: 'Franchise Details',
              completed:
                !!franchise.overview &&
                !!franchise.company &&
                !!franchise.country &&
                !!franchise.established &&
                !!franchise.startedFranchising &&
                franchise.franchisedUnits != null &&
                franchise.corporateUnits != null &&
                !!franchise.statesRegisteredIn &&
                franchise.franchisesInCanada !== undefined &&
                franchise.otherInternationalFranchises !== undefined,
              link: ROUTES.MY_PROFILE_GENERAL,
            },
            {
              title: 'Gallery',
              completed: !!franchise.photos && franchise.photos.length > 0,
              link: ROUTES.MY_PROFILE_GENERAL,
            },
            {
              title: 'Contact',
              completed:
                !!franchise.email &&
                !!franchise.phone &&
                !!franchise.firstName &&
                !!franchise.lastName &&
                !!franchise.workPhone &&
                !!franchise.corporateAddress &&
                !!franchise.corporateWebsite,
              link: ROUTES.MY_PROFILE_CONTACT,
            },
            {
              title: 'Investment Profile',
              completed:
                franchise.franchiseFee !== undefined &&
                franchise.minNetWorth !== undefined &&
                franchise.cashRequiredMin !== undefined &&
                // TCX-866: royalty and adFee are { type, value, note }; an
                // object is always defined/truthy, so both must check the
                // value or a cleared fee still counts as complete (TCX-865).
                isFeeFilled(franchise.royalty) &&
                isFeeFilled(franchise.adFee) &&
                !!franchise.specialDiscountPrograms &&
                franchise.item19 !== undefined &&
                franchise.visaCandidates !== undefined &&
                franchise.semiAbsenteeModel !== undefined &&
                franchise.b2b !== undefined &&
                franchise.homeBased !== undefined &&
                franchise.masterFranchiseOpportunities !== undefined &&
                franchise.veteranDiscount !== undefined,
              link: ROUTES.MY_PROFILE_FINANCIAL,
            },
            {
              title: 'Training and Support',
              completed:
                franchise.financialAssistance !== undefined &&
                franchise.siteSelectionAssistance !== undefined &&
                franchise.leaseNegotiationAssistance !== undefined &&
                franchise.recruitmentAssistance !== undefined &&
                franchise.coopAdvertising !== undefined &&
                !!franchise.training &&
                !!franchise.dayInTheLifeOfTheOwner &&
                !!franchise.whoIsAnIdealCandidate,
              link: ROUTES.MY_PROFILE_TRAINING,
            },
            {
              title: 'Referral Fee',
              // TCX-866: each commission leaf is now { type, value, note };
              // an object is always truthy, so these must check .value or
              // the completeness bar silently inflates. Rendering type/note
              // is TCX-867/868.
              completed:
                !!franchise.commissions &&
                !!franchise.commissions.areaMaster?.value &&
                !!franchise.commissions.resale?.value &&
                !!franchise.commissions.multipleUnit?.value &&
                !!franchise.commissions.singleUnit?.value &&
                !!franchise.commissions.veteran?.value,
              // &&
              // !!franchise.internationalCommissions &&
              // !!franchise.internationalCommissions.multipleUnit &&
              // !!franchise.internationalCommissions.areaMaster &&
              // !!franchise.internationalCommissions.resale &&
              // !!franchise.internationalCommissions.singleUnit,
              link: ROUTES.MY_PROFILE_REFERRAL_FEE,
            },
          ];
          dispatch(setProfileCompleteness(profileCompleteness));
        } else if (user.role === 'consultant') {
          const consultant = user as Consultant;
          const profileCompleteness: ProfileStep[] = [
            {
              title: 'General Info',
              completed:
                !!consultant.bio &&
                !!consultant.firstName &&
                !!consultant.lastName &&
                !!consultant.email &&
                !!consultant.experienceLevel &&
                !!consultant.groups &&
                !!consultant.country &&
                // !!consultant.bookingLink &&
                !!consultant.stateProvince &&
                !!consultant.city,
              link: ROUTES.MY_PROFILE_GENERAL,
            },
            {
              title: 'Payment Details',
              completed: !!consultant.rate,
              link: ROUTES.MY_PROFILE_PAYMENT,
            },
            {
              title: 'Rate',
              completed: !!consultant.rate,
              link: ROUTES.MY_PROFILE_PAYMENT,
            },
          ];
          dispatch(setProfileCompleteness(profileCompleteness));
        }
      }
    }, [user]);

    if (isLoading && !isPublic) {
      return <FullScreenLoader />;
    }

    return user || isPublic ? <WrappedComponent {...props} /> : null;
  };
}
